<template>
  <div id="app">
    <div class="full-screen">
      <div v-show="iframeUrl === ''" class="home">
        <div id="adult-button">
          <img class="image" :src="adultButtonThumbnail" />
          <div class="text">{{ $t("dashboardCatalog1Detail") }}</div>
          <div class="nav-button">
            <div>{{ $t("dashboardCatalog1Button") }}</div>
            <img src="./assets/arrow.png" />
          </div>
        </div>

        <div id="child-button">
          <img class="image" :src="childButtonThumbnail" />
          <div class="text">{{ $t("dashboardCatalog2Detail") }}</div>
          <div class="nav-button">
            <div>{{ $t("dashboardCatalog2Button") }}</div>
            <img src="./assets/arrow.png" />
          </div>
        </div>
      </div>

      <div v-show="iframeUrl !== ''" class="full-screen" style="background-color: #fff; display: flex; flex-flow: column; align-items: flex-start; justify-content: center; padding-left: 1rem;padding-right: 1rem;">
        <div id="home-button" v-show="iframeUrl !== ''" style="top: 0.5%; left: 0.5%; z-index: 2; display: flex; flex-flow: row; align-items: center;">
          <img style="width: 20%;" src="./assets/logo.png" />
          <div style="font-weight: bold; padding-left: 1rem; font-size: 1.5rem; color: #000">
            {{ $t("catalogBackButton") }}
          </div>
        </div>
        <iframe id="web-viewer" :src="iframeUrl" seamless="seamless" scrolling="no" frameBorder="0"></iframe>
      </div>
    </div>

    <div v-show="isScreenSaverRunning" id="screen-saver">
      <video id="video" loop :src="videoSrc">
        Your browser does not support the video tag.
      </video>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { VideoChunkDatabase, ImageSequenceDatabase, Chunk } from "./indexedDb";
import Dexie from "dexie";
import { FetchRange } from "./fetchUtil";

import adultsThumbCs from "./assets/Lego_Adults_cs.png";
import adultsThumbSk from "./assets/Lego_Adults_sk.png";

import childThumbCs from "./assets/Lego_Kids_cs.png";
import childThumbSk from "./assets/Lego_Kids_sk.png";

@Component({})
export default class App extends Vue {
  private videoUrl = "";
  private videoMuted = false;
  private videoInterval = 60000;
  private videoRetryInterval = 1800;
  private inactivityTimer = -1;
  private timer = -1;
  public videoSrc = "";
  public isScreenSaverRunning = false;
  public adultButtonThumbnail = adultsThumbCs;
  public childButtonThumbnail = childThumbCs;
  public iframeUrl = "";

  private catalog2Url = this.$t("catalog2.url") as string;
  private catalog2PageCount = parseInt(this.$t("catalog2.pageCount") as string, 10);
  private imageSequence: string[] = [];

  constructor() {
    super();

    const params = new URLSearchParams(window.location.search.slice(1));
    if (params.has("videoInterval")) {
      this.videoInterval =
        parseInt(params.get("videoInterval") || "") * 1000 || 60000;
    }
    if (params.has("videoRetryInterval")) {
      this.videoRetryInterval =
        parseInt(params.get("videoRetryInterval") || "") || 18000;
    }

    if (params.has("videoUrl")) {
      this.videoUrl = params.get("videoUrl") as string;
    }

    if (params.has("videoMuted")) {
      this.videoMuted =
        (params.get("videoMuted") as string) === "true" || false;
    }

    if (params.has("language")) {
      const lang = (params.get("language") as string) || "cs";

      if (lang === "sk") {
        this.$i18n.locale = "sk";
        this.adultButtonThumbnail = adultsThumbSk;
        this.childButtonThumbnail = childThumbSk;
      }
      this.catalog2Url = this.$t("catalog2.url") as string;
      this.catalog2PageCount = parseInt(this.$t("catalog2.pageCount") as string, 10);
    }
  }

  async cacheImageSequence(
    db: ImageSequenceDatabase,
    prefix: string,
    files: string[]
  ): Promise<void> {
    try {
      const all = await db.getAll();
      const outdatedImages = all
        .filter(pdf => !pdf.url.startsWith(prefix))
        .map(pdf => pdf.url);
      await Promise.all(
        outdatedImages.map(async url => {
          return await db.remove(url);
        })
      );
      // eslint-disable-next-line no-empty
    } catch { }
    // Pouze u prvního obrázku v sequenci se kontroluje verze.
    const firstUrl = files[0];
    let firstImage = await db.get(firstUrl);
    const etag = firstImage?.eTag ? firstImage?.eTag : "";
    const fetchedImage = await FetchRange.fetch(firstUrl, etag);
    if (fetchedImage.status === 200) {
      await db.put(fetchedImage);
    }

    firstImage = await db.get(firstUrl);
    if (!firstImage) {
      return;
    }

    // U zbytku obrázku se zkontroluje jestli jsou stažené a pokud jsou stažené,
    // tak kontroluje jestli je etag stejný jako u prvního.
    for (const url of files.slice(1)) {
      const cachedImage = await db.get(url);
      if (!cachedImage || cachedImage?.eTag !== firstImage.eTag) {
        // K dalším obrázkům není uložený etag, proto se nevyplňuje při stahování.
        const fetchedImage = await FetchRange.fetch(url, "");
        if (fetchedImage.status === 200) {
          // Uložení stejného etag jako má první obrázek kvůli optimalizaci requestů.
          fetchedImage.eTag = firstImage.eTag;
          await db.put(fetchedImage);
        }
      }
    }
    console.info("Image sequence downloaded.");
  }

  async cacheVideo(url: string) {
    if (!url) {
      return;
    }
    const dbName = "dex-lego";
    const db = new VideoChunkDatabase(dbName, 1);

    db.open()
      // Kontrola a stazeni videa
      .then(async () => {
        if (!db.meta || !db.videoChunks) {
          throw new Error("db meta or videoChunks is undefined");
        }
        const cachedMeta = await db.meta
          .where("url")
          .equals(url)
          .first();
        if (cachedMeta && cachedMeta.url === url) {
          // Video je jiz nacachovane
          console.info(`${url} is already cached`);
          return;
        }

        // Video je potreba znovu stahnout
        console.info(`${url} needs to be downloaded`);

        // Odstrani stare zaznamy z aktuální databaze
        await db.meta.clear();
        await db.videoChunks.clear();

        const chunkSize = 4194304;
        let offset = 0;

        let res = await fetch(url, {
          method: "HEAD"
        });

        const contentLength = parseInt(
          res.headers.get("Content-Length") || "",
          10
        );
        if (contentLength < 1) {
          throw new Error(`Content-length is empty for ${url}`);
        }

        const chunkCount = Math.trunc(contentLength / chunkSize) + 1;
        for (let i = 1; i <= chunkCount; i++) {
          const from = offset;
          const to = offset + chunkSize;
          // console.log(`before downloading bytes=${from}-${to}`);

          res = await fetch(url, {
            headers: new Headers({
              Range: `bytes=${from}-${to}`
            })
          });
          const b = await res.blob();
          await db.videoChunks.add({ blob: b, order: i, to, from });
          offset += chunkSize + 1;
          // console.log(`after downloading bytes=${from}-${to}`);
        }
        await db.meta.add({
          url,
          chunkCount,
          chunkSize
        });
        return;
      })
      // Nacteni videa z DB
      .then(async () => {
        if (!db.meta || !db.videoChunks) {
          throw new Error("db meta or videoChunks is undefined");
        }

        const cachedMeta = await db.meta
          .where("url")
          .equals(url)
          .first();
        if (!cachedMeta) {
          throw new Error(`no cached meta for ${url}`);
        }
        const buffer = [];
        for (let i = 1; i <= cachedMeta.chunkCount; i++) {
          const part = (
            await db.videoChunks
              .where("order")
              .equals(i)
              .first()
          )?.blob;
          if (!part) {
            throw new Error(`videoChunk "${i}" not found.`);
          }
          buffer.push(part);
        }
        return new Blob(buffer);
      })
      .then((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        this.videoSrc = url;
        // Zapnutí inactivity časovače, který kontroluje neaktivitu pouze u elementů, které nejsou v iframe.
        // Pokud se zobrazí iframe, tento časovač se vypne (destroyInactivityTime) a zapne se checkFocus.
        // Ten je aktivní tak dlouho, dokud je zobrazený iframe, pokud se zapne spořič, nebo se přijde zpět
        // na rozcestník, tak dojde k jeho vypnutí.
        this.inactivityTime();
        (document.getElementById(
          "video"
        ) as HTMLVideoElement).muted = this.videoMuted;
      })
      .catch(Dexie.MissingAPIError, e => {
        console.error("Couldn't find indexedDB API", e);
      })
      .catch("SecurityError", e => {
        console.error(
          "SeurityError - This browser doesn't like fiddling with indexedDB.",
          e
        );
      })
      .catch(async e => {
        console.error(e);
        if (e && e.name === "AbortError") {
          console.warn(
            `trying save video again after ${1000 *
            this.videoRetryInterval} seconds.`
          );
          db.close();
          const databases = await Dexie.getDatabaseNames();
          await Promise.all(
            databases.map(async name => {
              await Dexie.delete(name);
              return name;
            })
          );

          // Když se vykytne chyba, tak vyzkouším video uložit video znovu do DB.
          // Tato chyba nastává často, kdy se snažíme rychle po sobě uložit více nových videí,
          // protože po smazání obsahu v indexeddb stejně zůstane zabraná paměť zhruba dalších 10 minut.
          setTimeout(async () => {
            await this.cacheVideo(url);
          }, 1000 * this.videoRetryInterval);
        }
      });
  }

  private adultClick(event: Event) {
    this.destroyInactivityTime();
    event.preventDefault();
    event.stopPropagation();
    this.userInactivity = false;
    this.iframeUrl = this.$t("catalog1Url") as string;
    this.inactivityTimer = setInterval(() => {
      this.checkFocus("#web-viewer");
    }, 2000);
  }

  private childClick(event: Event) {
    this.destroyInactivityTime();
    event.preventDefault();
    event.stopPropagation();
    this.userInactivity = false;

    const iframe = document.getElementById(
      "web-viewer"
    ) as HTMLIFrameElement | null;
    if (!iframe) {
      throw new Error("missing iframe for child catalog");
    }
    iframe.addEventListener(
      "load",
      () => {
        iframe.contentWindow?.postMessage(this.imageSequence, "*");
      },
      { once: true }
    );

    this.iframeUrl = `pdf-viewer/index.html`;

    this.inactivityTimer = setInterval(() => {
      this.checkFocus("#web-viewer");
    }, 2000);
  }

  private goHome() {
    // zrušení timeut z checkFocus
    clearTimeout(this.timer);
    // zrušení intervalu checkFocus
    clearInterval(this.inactivityTimer);
    this.userInactivity = false;
    this.iframeUrl = "";
    this.inactivityTime();
  }

  mounted() {
    const catalog2ImageSequence = new Array(this.catalog2PageCount)
      .fill(undefined)
      .map((_v, idx) => {
        return new URL(
          `page-${(idx + 1).toString().padStart(3, "0")}.png`,
          this.catalog2Url
        ).href;
      })
      .slice(1);
    this.imageSequence = catalog2ImageSequence;

    const db = new ImageSequenceDatabase();
    this.cacheImageSequence(
      db,
      this.catalog2Url,
      catalog2ImageSequence.map(href => href)
    ).then(() => {
      const pages: string[] = [];
      Promise.all(
        catalog2ImageSequence.map(async href => {
          const img = await db.get(href);
          if (!img) {
            pages.push(href);
            return;
          }
          const blob = new Blob(
            img.chunks.map(ch => ch.blob),
            { type: "image/png" }
          );
          pages.push(URL.createObjectURL(blob));
        })
      ).finally(() => {
        this.imageSequence = pages;
      });
    });

    // Stažení videa, které se má přehrávat ve spořiči obrazovky
    this.cacheVideo(this.videoUrl);

    ["touchstart", "mousedown"].forEach(evt => {
      document
        .getElementById("adult-button")
        ?.addEventListener(evt, this.adultClick.bind(this));
      document
        .getElementById("child-button")
        ?.addEventListener(evt, this.childClick.bind(this));

      document
        .getElementById("home-button")
        ?.addEventListener(evt, this.goHome.bind(this));

      document
        .getElementById("screen-saver")
        ?.addEventListener(evt, this.hideScreenSaver.bind(this));
    });
  }

  destroyed() {
    ["touchstart", "mousedown"].forEach(evt => {
      document
        .getElementById("adult-button")
        ?.removeEventListener(evt, this.adultClick.bind(this));
      document
        .getElementById("child-button")
        ?.removeEventListener(evt, this.childClick.bind(this));

      document
        .getElementById("home-buton")
        ?.removeEventListener(evt, this.goHome.bind(this));

      document
        .getElementById("screen-saver")
        ?.removeEventListener(evt, this.hideScreenSaver.bind(this));
    });

    URL.revokeObjectURL(this.videoSrc);
    clearInterval(this.inactivityTimer);
  }

  private resetTimer() {
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(this.showScreensaver, this.videoInterval);
    // 1000 milliseconds = 1 second
  }

  private inactivityTime() {
    this.resetTimer();
    // DOM Events
    document.addEventListener("click", this.resetTimer);
  }

  private destroyInactivityTime() {
    clearTimeout(this.inactivityTimer);

    window.removeEventListener("load", this.resetTimer);
    // DOM Events
    document.removeEventListener("click", this.resetTimer);
  }

  private userInactivity = false;

  private checkFocus(selector: string) {
    // Kontrola jestli ma iframe focus, pokud ano tak ho zavolam blur, zrusim casoc (v pri zmene userInactivity). Jinak nastavim userInactivity na true a spustim casovac.
    if (document.activeElement == document.querySelector(selector)) {
      this.userInactivity = false;
      const d = document.activeElement as HTMLElement;
      d.blur();
    } else {
      this.userInactivity = true;
    }
  }

  @Watch("userInactivity")
  onUserInactivityChanged(value: boolean) {
    if (value === false) {
      clearTimeout(this.timer);
    } else {
      this.timer = setTimeout(this.showScreensaver, this.videoInterval);
    }
  }

  private refreshIframe() {
    this.iframeUrl = "";
  }

  private showScreensaver() {
    if (!this.videoSrc) {
      // Pokud neni video definovane, tak udelam jenom reload iframe
      this.refreshIframe();
      this.userInactivity = false;
      return;
    }

    clearInterval(this.inactivityTimer);
    const video = document.querySelector("#video") as HTMLVideoElement;
    video
      .play()
      .then(() => {
        this.isScreenSaverRunning = true;
        this.refreshIframe();
      })
      .catch(err => {
        console.error(err);
        this.isScreenSaverRunning = false;
        this.userInactivity = false;
        this.inactivityTime();
      });
  }

  private hideScreenSaver(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const video = document.querySelector("#video") as HTMLVideoElement;
    video.pause();
    this.isScreenSaverRunning = false;
    this.userInactivity = false;
    this.inactivityTime();
  }
}
</script>

<style lang="less">
@import "./fonts/cera-pro/cera-pro.less";

html,
body {
  overflow: hidden;
  width: 100%;
  height: 100%;
  font-family: "Cera Pro", sans-serif;
}

* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

#app {
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-image: url("./assets/background.png");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
}

#screen-saver {
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;

  width: 100%;
  height: 100%;
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;

  video {
    width: 100%;
  }
}

.full-screen {
  width: 100%;
  height: 100%;
}

#adult-button,
#child-button {
  display: flex;
  flex-flow: column;
  align-items: center;
}

#adult-button:active,
#child-button:active {
  opacity: 0.7;
}

.image {
  width: 84%;
  margin-bottom: 3rem;
}

.text {
  font-weight: lighter;
  color: #fff;
  text-align: center;
  margin-bottom: 3rem;
  font-size: 1.3rem;
  white-space: pre-wrap;
}

.home {
  display: flex;
  flex-flow: row;
  width: 100%;
  height: 100%;
  margin-top: 12rem;
  padding-left: 18rem;
  padding-right: 20rem;
  justify-content: space-between;
}

.nav-button {
  display: flex;
  flex-flow: row;
  width: 12rem;
  height: 4rem;
  background-color: #fff;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  font-size: 1.4rem;
  font-weight: bold;

  div {
    padding-right: 1rem;
  }
}

#web-viewer {
  width: 100%;
  height: 90%;
  background-color: #fff;
}
</style>
